import { useEffect, useState } from 'react';
import UserServ from '@api/Users/UserServ';

/** Quien no es una persona: lo que escribe el aseo diario del server. */
const SYSTEM_AUTHORS: Record<string, string> = { 'cron-daily': 'Automático' };

/**
 * Nombres de las personas detrás de unos `uid` (quién capturó cada gasto).
 * Una lectura por PERSONA distinta, nunca por renglón, y con la caché de
 * `UserServ.getUser` (TTL 10 min): una lista de 400 gastos de 12 personas son 12
 * lecturas la primera vez y 0 después. Un uid que no se pudo leer se queda sin
 * nombre (la pantalla decide qué pintar).
 */
const useUserNames = (uids: (string | undefined)[]): Record<string, string> => {
    const [names, setNames] = useState<Record<string, string>>({});
    const key = Array.from(new Set(uids.filter(Boolean) as string[]))
        .sort()
        .join('|');

    useEffect(() => {
        let alive = true;
        const pending = key ? key.split('|').filter((uid) => !(uid in names)) : [];
        if (pending.length === 0) return;
        void Promise.all(
            pending.map(async (uid) => {
                if (SYSTEM_AUTHORS[uid]) return [uid, SYSTEM_AUTHORS[uid]] as const;
                try {
                    const user = await UserServ.getUser(uid);
                    return [uid, user?.profile?.name || user?.email || ''] as const;
                } catch {
                    return [uid, ''] as const;
                }
            })
        ).then((entries) => {
            if (alive) setNames((current) => ({ ...current, ...Object.fromEntries(entries) }));
        });
        return () => {
            alive = false;
        };
    }, [key]);

    return names;
};

export default useUserNames;
